import React from 'react';
import './Map.css';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4lang_de_DE from "@amcharts/amcharts4/lang/de_DE";
import am4lang_it_IT from "@amcharts/amcharts4/lang/it_IT";

import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';

import { Municipalities, MunicipalityNameValues, NameType, Person, Sex } from 'lib/Types';

import geoData from 'data/municipalityMapGeojson.json';
import translationsJson from 'data/translations.json';
import municipalitiesJson from 'data/municipality.json';
import info from 'data/info.json';
import { isTouchInput, MobileDeviceContext } from 'lib/Screen';

am4core.useTheme(am4themes_animated);

const translations: Translations = translationsJson;
const municipalities: Municipalities = municipalitiesJson;

interface MapDataItem {
    id: string;
    name: string;
    value: number | undefined;
    valueStr: string;
    percent: string;
    color: string;
    opacity: number;
}

interface Props {
    type: NameType;
    person: Person | undefined;
    data: MunicipalityNameValues;
    onClick(municipalityId: string): void;
}

class Map extends React.Component<Props, {}> {
    static contextType = MobileDeviceContext;

    private map: am4maps.MapChart | null;
    private series: am4maps.MapPolygonSeries | null;
    private legend: am4core.Container | null;
    private divName: string;
    private bounds: number[];

    private static MinOpacity = 0.4;
    private static MaxOpacity = 1;
    private static NoDataColor = "#777";
    private static HoverColor = "#333";

    constructor(props: Props) {
        super(props);

        this.map = null;
        this.series = null;
        this.legend = null;
        this.divName = `map_${this.props.type}`;

        if (this.props.type === NameType.Firstname) {
            this.bounds = [0, 0.5, 1, 2, 3];
        } else {
            this.bounds = [0, 0.5, 1, 2, 3, 5];
        }
    }

    private createMap() {
        //console.log("creating map:", this.divName);

        const chart = am4core.create(this.divName, am4maps.MapChart);
        const chartLanguage = new am4core.Language();
        chartLanguage.locale = window.lang === "de" ? am4lang_de_DE : am4lang_it_IT;
        chart.numberFormatter.language = chartLanguage;
        chart.dateFormatter.language = chartLanguage;

        //chart.responsive.enabled = true;

        chart.geodata = geoData;
        //chart.reverseGeodata = true;
        chart.projection = new am4maps.projections.Mercator();

        const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());

        // Make map load polygon data (state shapes and names) from GeoJSON
        polygonSeries.useGeodata = true;

        // Set map data
        //polygonSeries.data = this.props.data;
        polygonSeries.data = Object.keys(this.props.data).length ? this.convertData()! : this.generateDefaultData();

        polygonSeries.tooltip!.getFillFromObject = false;
        polygonSeries.tooltip!.background.fill = am4core.color("#fafafa");
        polygonSeries.tooltip!.label.fill = am4core.color("#000");

        // Configure series tooltip
        const polygonTemplate = polygonSeries.mapPolygons.template;

        polygonTemplate.tooltipText = `[bold]{name}[/]`;
        polygonTemplate.nonScalingStroke = true;
        polygonTemplate.strokeWidth = 0.8;
        polygonTemplate.stroke = am4core.color("#fff");
        polygonTemplate.propertyFields.fill = "color";
        polygonTemplate.propertyFields.fillOpacity = "opacity";
        polygonTemplate.tooltipPosition = "fixed";

        // #########
        polygonTemplate.events.on("over", (event) => {
            if(!isTouchInput())
                handleHover(event.target);
        });

        polygonTemplate.events.on("hit", (event) => {
            if(this.context || isTouchInput()) {
                handleHover(event.target);
            } else {
                this.props.onClick((event.target.dataItem.dataContext as MapDataItem).id);
            }
        });

        function handleHover(mapPolygon: am4maps.MapPolygon) {
            const dataItem = mapPolygon.dataItem;
            if (dataItem.value !== undefined) {
                mapPolygon.tooltipText = `[bold]{name}[/]
                -------------------
                ${translations.count[window.lang]}: {valueStr}
                ${translations.percentPart[window.lang]}: {percent}%`;
                mapPolygon.tooltipHTML =
                    `<div class="map-tooltip">
                        <center><strong>{name}</strong></center>
                        <hr />
                        <table>
                            <tbody>
                                <tr>
                                    <th align="right">${translations.count[window.lang]}: </th>
                                    <td align="right">{valueStr}</td>
                                </tr>
                                <tr>
                                    <th align="right">${translations.percentPart[window.lang]}: </th>
                                    <td align="right">{percent}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>`;
            }
        }

        // Create hover state and set alternative fill color
        const hs = polygonTemplate.states.create("hover");
        hs.properties.fill = am4core.color(Map.HoverColor);
        hs.properties.fillOpacity = 1;

        chart.maxZoomLevel = 1;
        chart.seriesContainer.draggable = false;
        chart.seriesContainer.resizable = false;
        this.map = chart;
        this.series = polygonSeries;
    }

    private createLegend() {
        if (this.legend) {
            this.legend.dispose();
        }

        const color = am4core.color(this.getColor());
        const legend = this.map!.createChild(am4core.Container);
        legend.layout = "vertical";
        legend.x = am4core.percent(75);
        legend.y = am4core.percent(65);

        const createItem = (i: number, percent: number, isZero: boolean) => {
            const container = legend.createChild(am4core.Container);
            container.layout = "horizontal";
            const circle = container.createChild(am4core.Circle);
            circle.radius = 6;
            circle.align = "left";
            circle.verticalCenter = "middle";
            circle.fill = isZero ? am4core.color(Map.NoDataColor) : color;
            circle.fillOpacity = this.getOpacity(isZero ? 0 : i + 1, percent);
            const label = container.createChild(am4core.Label);
            label.align = "right";
            label.verticalCenter = "middle";
            label.marginLeft = 5;
            label.dy = 1;
            label.fontSize = "0.95rem";
            if (i + 1 < this.bounds.length && !isZero) {
                label.text = `${this.bounds[i].toLocaleString(window.lang)}% - ${this.bounds[i + 1].toLocaleString(window.lang)}%`;
            } else if (i === this.bounds.length - 1) {
                label.text = `> ${this.bounds[i].toLocaleString(window.lang)}%`;
            } else {
                label.text = `${this.bounds[i].toLocaleString(window.lang)}`;
            }
        }

        createItem(0, 0, true);

        for (let i = 0; i < this.bounds.length; i++) {
            createItem(i, this.bounds[i] + 0.01, false);
        }

        this.legend = legend;
    }

    private getColor() {
        return this.props.type === NameType.Lastname ? "#3f7e44" : (this.props.person!.sex! === Sex.Male ? "#19486a" : "#c5192d");
    }

    private getOpacity(count: number, percent: number) {
        const factor = (Map.MaxOpacity - Map.MinOpacity) / (this.bounds.length - 1);
        if (count === 0) {
            return Map.MaxOpacity;
        }
        if (count === -1) {
            return Map.MinOpacity;
        }

        for (let i = 0; i < this.bounds.length; i++) {
            if (percent > this.bounds[i]) {
                if (i + 1 < this.bounds.length) {
                    if (percent <= this.bounds[i + 1]) {
                        return Map.MinOpacity + factor * i;
                    }
                } else {
                    return Map.MaxOpacity;
                }
            }
        }

        return Map.MaxOpacity;
    }

    private convertData() {
        const inputData = this.props.data;
        if (Object.keys(inputData).length === 0) {
            return null;
        }

        const color = this.getColor();
        const data: MapDataItem[] = [];
        const zero = Number(0).toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1});
        for (let id in municipalities) {
            const mun = municipalities[id];
            if (id in inputData) {
                const count = inputData[id];
                const percent = (count / mun.population) * 100;
                const value = count === -1 ? `<${info.nameCountCutoff.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}` : `${count.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}`;
                const percentStr = count === -1 ? `<${((info.nameCountCutoff / mun.population) * 100).toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}` : `${percent.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}`;
                const opacity = this.getOpacity(count, percent);

                const item: MapDataItem = {
                    id: id,
                    name: mun.name[window.lang],
                    value: count,
                    valueStr: value,
                    percent: percentStr,
                    color: color,
                    opacity: opacity
                };

                data.push(item);
            } else {
                const item: MapDataItem = {
                    id: id,
                    name: mun.name[window.lang],
                    value: 0,
                    valueStr: "0",
                    percent: zero,
                    color: Map.NoDataColor,
                    opacity: 0.9
                };

                data.push(item);
            }
        }

        return data;
    }

    private generateDefaultData() {
        const data: MapDataItem[] = [];
        for (let id in municipalities) {
            const mun = municipalities[id];
            const item: MapDataItem = {
                id: id,
                name: mun.name[window.lang],
                value: undefined,
                valueStr: "",
                percent: "",
                color: Map.NoDataColor,
                opacity: 0.9
            };

            data.push(item);
        }

        return data;
    }

    componentDidMount() {
        this.createMap();
    }

    componentWillUnmount() {
        if (this.map) {
            //console.log("disposing map:", this.divName);
            this.legend?.dispose();
            this.map.dispose();
            this.map = null;
        }
    }

    componentDidUpdate(prevProps: Props) {
        const isNewData = () => {
            if (prevProps.data !== this.props.data && Object.keys(prevProps.data).length !== Object.keys(this.props.data).length)
                return true;
            for (let id in prevProps.data) {
                if (!(id in this.props.data) || prevProps.data[id] !== this.props.data[id])
                    return true;
            }

            return false;
        }

        //console.log("updating map:", this.divName);

        if (this.map) {
            if ((prevProps.person === undefined && this.props.person !== undefined) || (prevProps.person?.sex !== this.props.person?.sex) || (this.props.person !== undefined && !this.legend)) {
                this.createLegend();
            }
            if (isNewData()) {
                //console.log("updating map data:", this.divName);
                const data = this.convertData();
                if (data) {
                    this.series!.data = data;
                    this.series!.invalidateRawData();
                }
            }
        }
    }

    render() {
        return (
            <div>
                <div className={`map-title ${Object.keys(this.props.data).length ? "map-title-active" : ""}`}>
                    {this.props.type === NameType.Firstname ? translations.personsInSouthTyrolFirstname[window.lang] : translations.personsInSouthTyrolLastname[window.lang]} <b>{this.props.person?.name}{this.props.person && this.props.person.sex ? (this.props.person.sex === Sex.Male ? <MaleIcon className='gender-symbol' /> : <FemaleIcon className='gender-symbol' />) : ""} : {this.props.data[0] ? this.props.data["0"].toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1}) : ""}</b>
                </div>
                <div id={this.divName} className="map"></div>
            </div>
        );
    }
}

export default Map;